import store from '@/store';
import { RESOURCES } from '@/utils/constants';
import axios from 'axios';
import { validateResource } from './functions';

// Base API URL from store to ensure consistency
// const API_URL = store.state.apiUrl;
const API_URL = process.env.VUE_APP_API_URL;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  (config) => {
    // Get token - try multiple sources
    let token = null;

    // Try to get from userData
    if (store.state.userData && store.state.userData.token) {
      token = store.state.userData.token;
    }
    // Try to get from user module
    else if (store.state.user && store.state.user.token) {
      token = store.state.user.token;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { api };

// Generic API
export const APIService = {
  loadData: (resource, businessID, userID) => {
    if (!resource) {
      return Promise.reject(new Error('Resource is required'));
    }
    if (!businessID) {
      return Promise.reject(new Error('Business ID is required'));
    }

    if (!userID) {
      return Promise.reject(new Error('User ID is required'));
    }

    return api.get(`/${resource.name}/LoadData/${businessID}?userID=${userID}`);
  },

  // Get all items with count
  getWithAllCount: (resource, businessID) => {
    if (!resource) {
      throw new Error('resource is required');
    }
    if (!businessID) {
      throw new Error('Business ID is required');
    }
    if (!validateResource(resource.name)) {
      throw new Error('Invalid resource provided');
    }

    if (resource.name === RESOURCES.BUSINESS.name) {
      return api.get(`/${resource.name}/GetAllWithCount?businessID=${businessID}`);
    } else {
      return api.get(`/${resource.name}/GetAllWithCount/${businessID}`);
    }
  },

  // Get warehouse types with pagination
  getByPagination: (
    resource,
    businessID,
    params = {}
    // { pageNumber = 1, pageSize = 10, search = '', sortField = 'id', sortOrder = 'desc' } = {}
  ) => {
    if (!resource) {
      throw new Error('resource is required');
    }
    if (!businessID) {
      throw new Error('Business ID is required');
    }
    if (!validateResource(resource.name)) {
      throw new Error('Invalid resource provided');
    }

    let url = `/${resource.name}/GetByPagination/${businessID}`;

    if (['Cities', 'Countries', 'Forms'].includes(resource.name)) {
      url = `/${resource.name}/GetByPagination?businessID=${businessID}`;
    } else if (['Users'].includes(resource.name)) {
      url = `/${resource.name}/GetPaginationWithCount/${businessID}`;
    } else if (resource.name === RESOURCES.BUSINESS.name) {
      url = `/${resource.name}/GetByPagination?businessID=${businessID}`;
    }
    // else if ([RESOURCES.FORMS.name].includes(resource.name)) {
    //   url = `/${resource.name}/GetByPagination`;
    // }

    if (!('pageNumber' in params)) {
      params.pageNumber = 1;
    }
    if (!('pageSize' in params)) {
      params.pageSize = 10;
    }
    if (!('search' in params)) {
      params.search = '';
    }
    if (!('sortField' in params)) {
      params.sortField = 'id';
    }
    if (!('sortOrder' in params)) {
      params.sortOrder = 'desc';
    }

    // if (pageNumber !== 1) params.pageNumber = pageNumber;
    // if (pageSize !== 10) params.pageSize = pageSize;
    // if (search?.trim()) params.search = search.trim();
    // if (sortField !== 'id') params.sortField = sortField;
    // if (sortOrder !== 'desc') params.sortOrder = sortOrder;

    return api.get(url, { params });
  },

  getById: async (resource, id, businessID, userID) => {
    try {
      if (!resource) {
        throw new Error('resource is required');
      }
      if (!id) {
        throw new Error('ID is required');
      }
      if (!businessID) {
        throw new Error('Business ID is required');
      }
      if (!userID) {
        throw new Error('User ID is required');
      }
      if (!validateResource(resource.name)) {
        throw new Error('Invalid resource provided');
      }

      // Check if resource is Employees, which has a different endpoint structure
      if (
        [
          RESOURCES.INVENTORY_ITEMS.name,
          RESOURCES.PRICE_LIST.name,
          RESOURCES.INTERNAL_DELIVERY.name,
          RESOURCES.DELIVERY_CHALLAN.name,
          RESOURCES.PROVINCES.name,
          RESOURCES.TRANSFERS.name,
          RESOURCES.BUDGETS.name,
          RESOURCES.RECEIPT_CREDIT_VOUCHER.name,
          RESOURCES.PAYMENTS_DEBIT_VOUCHER.name,
        ].includes(resource.name)
      ) {
        return api.get(`/${resource.name}/LoadSelectedData/${businessID}`, {
          params: {
            id,
            userID,
          },
        });
      } else if ([RESOURCES.USERS.name, RESOURCES.EXPENSE_ENTRIES.name].includes(resource.name)) {
        return api.get(`/${resource.name}/LoadSelectedData/${id}/${businessID}`);
      } else if (
        [
          RESOURCES.DUE_RECEIVABLE_SCHEDULES.name,
          RESOURCES.REQUISITIONS.name,
          RESOURCES.REQUISITION_QUOTATIONS.name,
        ].includes(resource.name)
      ) {
        return api.get(`/${resource.name}/LoadSelectedData/${id}/${businessID}?userID=${userID}`);
      } else if (
        [
          RESOURCES.LOSS_ADJUSTMENT.name,
          RESOURCES.SALES_ORDERS.name,
          RESOURCES.GENERAL_ISSUANCE.name,
          RESOURCES.TEMPORARY_ISSUANCE.name,
          RESOURCES.STOCK_INPUT.name,
          RESOURCES.PHYSICAL_STOCK_TAKE.name,
          RESOURCES.PURCHASE_ORDERS.name,
          RESOURCES.GOOD_RECEIVING_NOTE.name,
          RESOURCES.CHARTS_OF_ACCOUNTS.name,
          RESOURCES.PURCHASE_INVOICE.name,
          RESOURCES.PURCHASE_RETURN.name,
          RESOURCES.SALES_RETURN.name,
          RESOURCES.JOURNAL_ENTRIES.name,
          RESOURCES.FUEL_VOUCHERS.name,
          RESOURCES.MONTHLY_PAYMENT_SCHEDULES.name,
          RESOURCES.POSTDATED_CHEQUES.name,
          RESOURCES.PHYSICAL_CASH_VERIFICATIONS.name,
        ].includes(resource.name)
      ) {
        return api.get(
          `/${resource.name}/LoadSelectedData/${businessID}?id=${id}&userID=${userID}`
        );
      } else if ([RESOURCES.SALES_INVOICE.name].includes(resource.name)) {
        return api.get(
          `/${resource.name}/LoadSelectedData/${businessID}?id=${id}&userID=${userID}`
        );
      } else if ([RESOURCES.METER_READINGS.name].includes(resource.name)) {
        return api.get(`/${resource.name}/LoadSelectedData/${businessID}?id=${id}`);
      } else {
        // Default endpoint structure for other resources
        return api.get(`/${resource.name}/LoadSelectedData/${businessID}/${id}?userID=${userID}`);
      }
    } catch (error) {
      throw error;
    }
  },

  save: async (resource, payload, businessID, userID) => {
    try {
      if (!resource) {
        throw new Error('resource is required');
      }
      if (!userID) {
        throw new Error('UserID is required');
      }
      if (!validateResource(resource.name)) {
        throw new Error('Invalid resource provided');
      }

      let url = `/${resource.name}/Save/${businessID}?userID=${userID}`;

      if (
        [
          RESOURCES.VENDOR_TYPES.name,
          RESOURCES.VENDOR_CATEGORIES.name,
          RESOURCES.INVENTORY_BRAND.name,
          RESOURCES.INVENTORY_CATEGORY.name,
          RESOURCES.INVENTORY_COMMODITY.name,
          RESOURCES.INVENTORY_COMMODITY_GROUP.name,
          RESOURCES.INVENTORY_SUB_GROUP.name,
          RESOURCES.INVENTORY_UNIT.name,
          RESOURCES.MANUFACTURERS.name,
          RESOURCES.FLAGS.name,
          RESOURCES.CARTON.name,
          RESOURCES.ORIGIN.name,
          RESOURCES.STYLE.name,
          RESOURCES.MODEL.name,
          RESOURCES.SIZE.name,
          RESOURCES.COLOR.name,
          RESOURCES.CITIES.name,
          RESOURCES.COUNTRIES.name,
          RESOURCES.VENUES.name,
        ].includes(resource.name)
      ) {
        url = `/${resource.name}/Save?userID=${userID}`;
      }

      if ([RESOURCES.USERS.name].includes(resource.name)) {
        url = `/${resource.name}/SaveUser?userID=${userID}`;
      }

      if ([RESOURCES.VENDORS.name].includes(resource.name)) {
        url = `/${resource.name}/Save/${businessID}`;
      }

      const response = await api.post(url, payload);
      return response;
    } catch (error) {
      throw error;
    }
  },

  delete: async (resource, id, businessID, userID) => {
    try {
      if (!resource) {
        throw new Error('resource is required');
      }
      if (!id) {
        throw new Error('ID is required');
      }
      if (!businessID) {
        throw new Error('Business ID is required');
      }
      if (!userID) {
        throw new Error('User ID is required');
      }
      if (!validateResource(resource.name)) {
        throw new Error('Invalid resource provided');
      }

      let url = `/${resource.name}/Delete/${id}/${businessID}?userID=${userID}`;
      if (
        [
          RESOURCES.INVENTORY_ITEMS.name,
          RESOURCES.PRICE_LIST.name,
          RESOURCES.INTERNAL_DELIVERY.name,
        ].includes(resource.name)
      ) {
        url = `/${resource.name}/Delete/${businessID}?id=${id}&userID=${userID}`;
      } else if ([RESOURCES.CUSTOMERS.name].includes(resource.name)) {
        url = `/${resource.name}/${id}`;
      } else if (
        [
          RESOURCES.EMPLOYEES_DESIGNATIONS.name,
          RESOURCES.VENUES.name,
          RESOURCES.CUSTOMER_TYPES.name,
          RESOURCES.CUSTOMER_GROUPS.name,
          RESOURCES.VENDOR_TYPES.name,
          RESOURCES.VENDOR_CATEGORIES.name,
          RESOURCES.WAREHOUSES.name,
          RESOURCES.WAREHOUSE_TYPES.name,
          RESOURCES.AREAS.name,
          RESOURCES.CHARTS_OF_ACCOUNTS.name,
        ].includes(resource.name)
      ) {
        url = `/${resource.name}/Delete/${id}/${businessID}?userID=${userID}`;
      } else if ([RESOURCES.USERS.name].includes(resource.name)) {
        url = `/${resource.name}/DeleteUser/${id}/${businessID}?userID=${userID}`;
      } else if (
        [RESOURCES.EMPLOYEES_DEPARTMENTS.name, RESOURCES.INVENTORY_OPENING_STOCK.name].includes(
          resource.name
        )
      ) {
        url = `/${resource.name}/Delete/${businessID}?id=${id}&userID=${userID}`;
      } else if ([RESOURCES.COUNTRIES.name, RESOURCES.CITIES.name].includes(resource.name)) {
        url = `/${resource.name}/Delete/${id}?userID=${userID}`;
      } else if ([RESOURCES.PROVINCES.name].includes(resource.name)) {
        url = `/${resource.name}/Delete/${businessID}?id=${id}&userID=${userID}`;
      } else if (
        [
          RESOURCES.EMPLOYEES.name,
          RESOURCES.VENDORS.name,
          RESOURCES.ROLES.name,
          RESOURCES.HS_CODE.name,
          RESOURCES.CURRENCIES.name,
        ].includes(resource.name)
      ) {
        url = `/${resource.name}/Delete/${businessID}/${id}?userID=${userID}`;
      } else if ([RESOURCES.CARTON.name].includes(resource.name)) {
        url = `/${resource.name}/Delete?id=${id}&businessID=${businessID}&userID=${userID}`;
      }

      const response = await api.delete(url);

      return response;
    } catch (error) {
      throw error;
    }
  },

  export: async (resource, businessID, userID, params) => {
    if (!resource) {
      throw new Error('resource is required');
    }
    if (!businessID) {
      throw new Error('Business ID is required');
    }

    // Make userID validation conditional based on resource type
    const resourcesWithoutUserID = ['EmployeesDepartments', 'PostdatedCheques'];
    if (!userID && !resourcesWithoutUserID.includes(resource.name)) {
      throw new Error('User ID is required');
    }

    if (!validateResource(resource.name)) {
      throw new Error('Invalid resource provided');
    }

    try {
      let url = `${resource.name}/Export/${businessID}`;

      // Add userID to query parameters if provided and required
      if (userID) {
        url += `?userID=${userID}`;

        // Add searchTerm to query parameters if provided
        // if (params && params.searchTerm && params.searchTerm.trim() !== '') {
        //   url += `&searchTerm=${encodeURIComponent(params.searchTerm.trim())}`;
        // }
      }
      // No userID but has searchTerm
      // else if (params && params.searchTerm && params.searchTerm.trim() !== '') {
      //   url += `?searchTerm=${encodeURIComponent(params.searchTerm.trim())}`;
      // }

      if ([RESOURCES.COUNTRIES.name, RESOURCES.CITIES.name].includes(resource.name)) {
        url = `/${resource.name}/Export`;
      }
      if ([RESOURCES.POSTDATED_CHEQUES.name].includes(resource.name)) {
        url = `/${resource.name}/Export/${businessID}`;
      }

      if ([RESOURCES.LOGS_DATA.name].includes(resource.name)) {
        url = `/${resource.name}/Export?businessID=${businessID}&userID=${userID}`;
      }

      const response = await api.get(url, { userID, params });
      return response;
    } catch (error) {
      throw error; // Re-throw to allow proper error handling
    }
  },

  approveReject: async (resource, id, businessID, userID, status, payload) => {
    try {
      if (!resource) {
        throw new Error('resource is required');
      }
      if (!id) {
        throw new Error('ID is required');
      }
      if (!businessID) {
        throw new Error('Business ID is required');
      }
      if (!userID) {
        throw new Error('User ID is required');
      }
      if (!validateResource(resource.name)) {
        throw new Error('Invalid resource provided');
      }

      const response = await api.post(
        `/${resource.name}/${status}/${businessID}?id=${id}&userID=${userID}`,
        payload
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateStatus: async (resource, id, status, businessID, userID) => {
    const payload = {
      userID,
      isActive: status,
    };

    let url = '';
    let response = null;

    if (
      [
        RESOURCES.VENDORS.name,
        RESOURCES.EMPLOYEES.name,
        RESOURCES.CUSTOMERS.name,
        RESOURCES.INVENTORY_ITEMS.name,
        RESOURCES.STATION_TYPE.name,
        RESOURCES.SALES_ORDER_TYPE.name,
        RESOURCES.SALES_ORDERS.name,
        RESOURCES.PROJECTS.name,
        RESOURCES.WAREHOUSES.name,
        RESOURCES.SALES_INVOICE.name,
        RESOURCES.BANK.name,
        RESOURCES.BANK_PROFILE.name,
        RESOURCES.ACCOUNT_ROUTES.name,
        RESOURCES.STOCK_INPUT.name,
        RESOURCES.GENERAL_ISSUANCE.name,
        RESOURCES.TEMPORARY_ISSUANCE.name,
        RESOURCES.EMPLOYEES_DEPARTMENTS.name,
        RESOURCES.TRANSFERS.name,
        RESOURCES.FUNDS.name,
        RESOURCES.JOURNAL_ENTRIES.name,
        RESOURCES.BUDGETS.name,
        RESOURCES.FUEL_VOUCHERS.name,
        RESOURCES.DUE_RECEIVABLE_SCHEDULES.name,
        RESOURCES.MONTHLY_PAYMENT_SCHEDULES.name,
        RESOURCES.EXPENSE_ENTRIES.name,
        RESOURCES.RECEIPT_CREDIT_VOUCHER.name,
        RESOURCES.PAYMENTS_DEBIT_VOUCHER.name,
      ].includes(resource.name)
    ) {
      url = `/${resource.name}/SetStatus/${businessID}?id=${id}&userID=${userID}&isActive=${status}`;
      response = await api.post(url, payload);
    } else if (
      [
        RESOURCES.EMPLOYEES_DESIGNATIONS.name,
        RESOURCES.LOSS_ADJUSTMENT.name,
        RESOURCES.CHARTS_OF_ACCOUNTS.name,
      ].includes(resource.name)
    ) {
      url = `/${resource.name}/SetStatus/${businessID}?id=${id}&isActive=${status}&userID=${userID}`;
      response = await api.post(url, payload);
    } else if (
      [
        RESOURCES.USERS.name,
        RESOURCES.SALES_RETURN.name,
        RESOURCES.CUSTOMER_TYPES.name,
        RESOURCES.CUSTOMER_GROUPS.name,
        RESOURCES.VENDOR_TYPES.name,
        RESOURCES.VENDOR_CATEGORIES.name,
        RESOURCES.WAREHOUSE_TYPES.name,
      ].includes(resource.name)
    ) {
      url = `/${resource.name}/SetStatus/${businessID}/${id}?isActive=${status}&userID=${userID}`;
      response = await api.patch(url, payload);
    } else {
      url = `/${resource.name}/SetStatus/${businessID}/${id}`;
      response = await api.patch(url, payload);
    }

    // const response = await api.patch(url, payload);

    if (response.data?.status === 'success') {
      return response;
    } else {
      throw new Error('Error updating status');
    }
  },

  employeeReq: async (resource, payload, businessID, userID) => {
    if (!resource) {
      throw new Error('resource is required');
    }
    if (!payload) {
      throw new Error('payload is required');
    }
    if (!businessID) {
      throw new Error('Business ID is required');
    }
    if (!userID) {
      throw new Error('User ID is required');
    }

    const response = await api.post(
      `/${resource.name}/CreateEmployeeRequest/${businessID}?userID=${userID}`,
      payload
    );
    return response;
  },

  import: async (resource, businessID, userID) => {
    const response = await api.post(
      `/${resource.name}/BulkImportOpeningStock/${businessID}?userID=${userID}`
    );
    return response;
  },

  batchUpload: async (resource, formData, businessID, userID) => {
    try {
      if (!resource) {
        throw new Error('Resource is required');
      }
      if (!formData) {
        throw new Error('Form data is required');
      }
      if (!businessID) {
        throw new Error('Business ID is required');
      }
      if (!userID) {
        throw new Error('User ID is required');
      }
      if (!validateResource(resource.name)) {
        throw new Error('Invalid resource provided');
      }

      const url = `/${resource.name}/BatchUpload/${businessID}?userID=${userID}`;

      // Use axios directly with different content type for file upload
      return api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      throw error;
    }
  },

  filterData: async (resource, businessID, filterPayload, userID) => {
    let url = `/${resource.name}/FilterData/${businessID}?userID=${userID}`;

    if (['Vendors'].includes(resource.name)) {
      url = `/${resource.name}/Filter/${businessID}?userID=${userID}`;
    }

    const response = await api.post(url, filterPayload);
    return response.data;
  },

  // Generic POST method for custom endpoints
  post: async (url, payload, options = {}) => {
    try {
      if (!url) {
        throw new Error('URL is required');
      }
      return await api.post(url, payload, options);
    } catch (error) {
      throw error;
    }
  },

  uploadFile: async (file) => {
    if (!file) {
      throw new Error('File is required');
    }

    if (typeof file !== 'object') {
      throw new Error('File must be an object');
    }

    let formData = new FormData();
    var axios = require('axios');

    formData.append('file', file);

    return new Promise((resolve, reject) => {
      axios
        .post('https://upload.myskool.app', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          resolve(response.data.myresp[0].path);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
};
